/**
 * AI Assistant Chat Widget
 * Floating chat widget with conversation history and suggestions
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Zoom,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import {
  SmartToy as AiIcon,
  Close as CloseIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Minimize as MinimizeIcon,
  OpenInFull as MaximizeIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { fetchWithAuth } from '../lib/api';
import { useBilling } from '../hooks/useBilling';
import UpgradeModal from './UpgradeModal';

// Suggested questions
const SUGGESTED_QUESTIONS = [
  'Πόσα οφείλω συνολικά στους υπαλλήλους;',
  'Ποιος δουλεύει αυτή την εβδομάδα;',
  'Πώς προσθέτω νέα βάρδια;'
];

export default function AiAssistant() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  const { hasFeature, isLoading: billingLoading } = useBilling();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  
  // State
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when new message arrives
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when dialog opens
  useEffect(() => {
    if (open && !minimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, minimized]);

  // Load conversation history on open
  useEffect(() => {
    if (open && messages.length === 0) {
      loadHistory();
    }
  }, [open]);

  // Load history from backend
  const loadHistory = async () => {
    try {
      const response = await fetchWithAuth('/api/ai/history');
      if (response.ok) {
        const history = await response.json();
        // Convert to messages format (reverse order since we get DESC)
        const historyMessages = [];
        history.reverse().forEach(conv => {
          historyMessages.push({
            id: `q-${conv.id}`,
            role: 'user',
            content: conv.question,
            timestamp: conv.created_at
          });
          historyMessages.push({
            id: `a-${conv.id}`,
            role: 'assistant',
            content: conv.answer,
            conversationId: conv.id,
            feedback: conv.feedback,
            timestamp: conv.created_at
          });
        });
        setMessages(historyMessages);
      }
    } catch (err) {
      console.error('Error loading AI history:', err);
    }
  };

  // Send message to AI
  const sendMessage = async (questionText = null) => {
    const question = questionText || input.trim();
    if (!question || loading) return;

    setInput('');
    setError(null);

    // Add user message
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = messages.slice(-6).map(m => ({
        role: m.role,
        content: m.content
      }));

      const response = await fetchWithAuth('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, conversationHistory })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Σφάλμα επικοινωνίας');
      }

      const data = await response.json();
      
      // Add AI response
      const aiMessage = {
        id: `ai-${Date.now()}`,
        role: 'assistant',
        content: data.answer,
        conversationId: data.conversationId,
        tokensUsed: data.tokensUsed,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);

    } catch (err) {
      console.error('AI Error:', err);
      setError(err.message);
      // Remove the user message if there was an error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id));
    } finally {
      setLoading(false);
    }
  };

  // Handle feedback
  const handleFeedback = async (conversationId, feedback) => {
    if (!conversationId) return;
    
    try {
      await fetchWithAuth('/api/ai/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationId, feedback })
      });
      
      // Update local state
      setMessages(prev => prev.map(m => 
        m.conversationId === conversationId 
          ? { ...m, feedback } 
          : m
      ));
    } catch (err) {
      console.error('Error saving feedback:', err);
    }
  };

  // Clear conversation
  const handleClear = () => {
    setMessages([]);
    setError(null);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (billingLoading) return null;

  const handleOpen = () => {
    if (!hasFeature('aiAssistant')) {
      setUpgradeOpen(true);
      return;
    }
    setOpen(true);
  };

  return (
    <>
      {/* Floating Action Button */}
      <Zoom in={!open}>
        <Fab
          color="primary"
          onClick={handleOpen}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 1000,
            width: { xs: 56, md: 64 },
            height: { xs: 56, md: 64 },
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        >
          <AiIcon sx={{ fontSize: { xs: 28, md: 32 } }} />
        </Fab>
      </Zoom>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            position: isMobile ? 'fixed' : 'fixed',
            bottom: isMobile ? 0 : 24,
            right: isMobile ? 0 : 24,
            m: 0,
            width: isMobile ? '100%' : 400,
            maxWidth: isMobile ? '100%' : 400,
            height: minimized ? 'auto' : (isMobile ? '100%' : 600),
            maxHeight: isMobile ? '100%' : 600,
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden'
          }
        }}
        hideBackdrop={!isMobile}
        disableScrollLock={!isMobile}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon />
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              AI Assistant
            </Typography>
          </Box>
          <Box>
            {!isMobile && (
              <IconButton
                size="small"
                onClick={() => setMinimized(!minimized)}
                sx={{ color: 'white', mr: 0.5 }}
              >
                {minimized ? <MaximizeIcon /> : <MinimizeIcon />}
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: 'white', mr: 0.5 }}
              title="Νέα συνομιλία"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Content */}
        {!minimized && (
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              height: '100%',
              bgcolor: isDarkMode ? 'grey.900' : 'grey.50'
            }}
          >
            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              {/* Welcome message if no messages */}
              {messages.length === 0 && !loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <AiIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Γεια σου! 👋
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Είμαι ο AI Assistant του Palmera. Ρώτα με οτιδήποτε για την εφαρμογή ή τα δεδομένα σου!
                  </Typography>
                  
                  {/* Suggested questions */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Δοκίμασε μία από αυτές τις ερωτήσεις:
                  </Typography>
                  <Stack direction="column" spacing={1} alignItems="center">
                    {SUGGESTED_QUESTIONS.map((q, i) => (
                      <Chip
                        key={i}
                        label={q}
                        onClick={() => sendMessage(q)}
                        clickable
                        variant="outlined"
                        color="primary"
                        sx={{ maxWidth: '100%' }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Messages list */}
              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      maxWidth: '85%',
                      bgcolor: message.role === 'user' 
                        ? 'primary.main' 
                        : (isDarkMode ? 'grey.800' : 'white'),
                      color: message.role === 'user' ? 'white' : 'text.primary',
                      borderRadius: 2,
                      borderTopRightRadius: message.role === 'user' ? 0 : 2,
                      borderTopLeftRadius: message.role === 'assistant' ? 0 : 2
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        '& ul, & ol': { pl: 2, my: 1 },
                        '& li': { mb: 0.5 }
                      }}
                    >
                      {message.content}
                    </Typography>

                    {/* Feedback for AI messages */}
                    {message.role === 'assistant' && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'flex-end',
                        gap: 0.5, 
                        mt: 1,
                        pt: 0.5
                      }}>
                        <Tooltip title="Χρήσιμο">
                          <IconButton
                            size="small"
                            onClick={() => handleFeedback(message.conversationId || message.id, 1)}
                            sx={{ 
                              color: message.feedback === 1 ? 'success.main' : 'text.secondary'
                            }}
                          >
                            <ThumbUpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Όχι χρήσιμο">
                          <IconButton
                            size="small"
                            onClick={() => handleFeedback(message.conversationId || message.id, -1)}
                            sx={{ 
                              color: message.feedback === -1 ? 'error.main' : 'text.secondary'
                            }}
                          >
                            <ThumbDownIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Paper>
                </Box>
              ))}

              {/* Loading indicator */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: isDarkMode ? 'grey.800' : 'white',
                      borderRadius: 2,
                      borderTopLeftRadius: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Σκέφτομαι...
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Error message */}
              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box
              sx={{
                p: 2,
                bgcolor: isDarkMode ? 'grey.800' : 'white',
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  size="small"
                  placeholder="Ρώτα με κάτι..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  multiline
                  maxRows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': { bgcolor: 'grey.300' }
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        featureName="AI Assistant"
        message="Το AI Assistant είναι διαθέσιμο στα πλάνα Pro και Business."
      />
    </>
  );
}

